/**
 * Alpine.js directive: dragToScroll
 *
 * Enables drag-to-scroll behavior for the bound element.
 *
 * - Works on desktop (mouse drag)
 * - Skips touch devices
 * - Supports 'x', 'y', or 'both' axes
 * - Leaves native scrollbars intact
 *
 * @param {HTMLElement} el The scrollable container
 * @param {Object} param1 Alpine directive param object
 * @param {String} param1.expression Optional: 'x', 'y', or 'both' (default 'both')
 *
 * @return {void}
 */
export function dragToScroll(el, { expression }) {
    // Check if it is a touch device
    const isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || (navigator.msMaxTouchPoints > 0);

    if (isTouchDevice) {
        const touchClass = 'scroll-native';

        // Add the scroll native class for touch devices
        el.classList.add(touchClass);

        // Directive returns early, disabling drag functionality
        return () => {
            el.classList.remove(touchClass);
        };
    }

    // Core Drag Logic (only runs if NOT a touch device)

    // State and setup variables
    let lastClientX = 0;
    let lastClientY = 0;
    let isDragging = false;
    let scroller = el;

    const axes = expression || 'both';
    const scrollX = axes.includes('x') || axes === 'both';
    const scrollY = axes.includes('y') || axes === 'both';

    // Define Handlers
    const handleMouseDown = (e) => {
        if (e.target.closest('[nochilddrag]')) {
            return;
        }
        isDragging = true;
        lastClientX = e.clientX;
        lastClientY = e.clientY;
        e.preventDefault();
    };

    const handleMouseUp = () => {
        isDragging = false;
    };

    const handleMouseMove = (e) => {
        if (!isDragging) return;

        const deltaX = lastClientX - e.clientX;
        const deltaY = lastClientY - e.clientY;

        lastClientX = e.clientX;
        lastClientY = e.clientY;

        if (scrollX) { scroller.scrollLeft += deltaX; }
        if (scrollY) { scroller.scrollTop += deltaY; }

        // Body/Root element handling
        if (scroller === document.body) {
            if (scrollX) { document.documentElement.scrollLeft += deltaX; }
            if (scrollY) { document.documentElement.scrollTop += deltaY; }
        }
    };

    // Setup Event Listeners
    el.addEventListener('mousedown', handleMouseDown, { passive: false });
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('mousemove', handleMouseMove);

    // Cleanup Function (runs if NOT a touch device)
    return () => {
        el.removeEventListener('mousedown', handleMouseDown);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('mousemove', handleMouseMove);
    };
}
